import React, { useState } from "react";
import apiService from "../../services/api";
import InnerLayout from "../../layouts/InnerLayout";
import {
  Box,
  Typography,
  Container,
  TextField,
  Button,
  FormHelperText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid2";
import InnerBanner from "../../images/inner-banner.jpg";
import CallforAction from "../../components/call_for_action/CallForActtion.js";
import TermsConditions from "../puja_request/TermsConditions.js";
import { useAppSettings } from "../../contexts/AppSettingsContext";

const validationSchema = yup.object({
  request_id: yup.string().required("Request ID is required"),
  email: yup
    .string()
    .email("Invalid email format")
    .required("Email is required"),
  name: yup.string().required("Requestor Name is required"),
});

const getOrdinal = (day) => {
  const suffixes = ["th", "st", "nd", "rd"];
  const value = day % 100;
  return `${day}${suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0]}`;
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const month = date.toLocaleString('default', { month: 'long' });
  const day = date.getDate();
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const isAM = hours < 12;
  const hour12 = hours % 12 || 12;
  const formattedTime = `${hour12}:${minutes < 10 ? '0' + minutes : minutes} ${isAM ? 'AM' : 'PM'}`;
  return `${month} ${getOrdinal(day)}, ${year}`;
};

const timeSlots = [
  "05:00 - 06:00 AM",
  "06:00 - 07:00 AM",
  "07:00 - 08:00 AM",
  "08:00 - 09:00 AM",
  "09:00 - 10:00 AM",
  "10:00 - 11:00 AM",
  "11:00 - 12:00 PM",
  "12:00 - 01:00 PM",
  "01:00 - 02:00 PM",
  "02:00 - 03:00 PM",
  "03:00 - 04:00 PM",
  "04:00 - 05:00 PM",
  "05:00 - 06:00 PM",
  "06:00 - 07:00 PM",
  "07:00 - 08:00 PM",
];

const changeRequestSchema = yup.object({
  name: yup.string().required("Requestor Name is required"),
  new_event_date: yup.string().required("New Event Date is required"),
  time_of_event: yup.string().required("Time of Event is required"),
  comments: yup.string().required("Comment is required"),
  terms_accepted: yup.boolean().oneOf([true], "You must accept the terms."),
});

function ChangePujaRequest() {
  const [bookingDetails, setBookingDetails] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();
  const { changeAndCancelNote } = useAppSettings() || {};

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({ resolver: yupResolver(validationSchema) });

  const {
    register: registerChange,
    handleSubmit: handleSubmitChange,
    formState: { errors: errorsChange, isSubmitting }, // Add isSubmitting
  } = useForm({ resolver: yupResolver(changeRequestSchema) });

  const handleSearch = async (data) => {
    try {
      const response = await apiService.getPoojaDetails(data);
      if (response.data.success) {
        if (response.data.data.name.toLowerCase() === data.name.toLowerCase()) {
          setBookingDetails(response.data.data);
          setErrorMessage("");
        } else {
          setErrorMessage("The provided name does not match the booking name.");
          setBookingDetails(null);
        }
      } else {
        setErrorMessage(
          "No request found with the provided Request ID and Email and name."
        );
        setBookingDetails(null);
      }
    } catch (error) {
      setErrorMessage("Error fetching booking details. Please try again.");
      setBookingDetails(null);
    }
  };

  console.log("booking details are:", bookingDetails);

  const handleChangeRequest = async (data) => {
    try {
      const payload = {
        request_id: bookingDetails.puja_request_id,
        name: data.name,
        email: bookingDetails.email,
        new_event_date: data.new_event_date,
        time_of_event: data.time_of_event,
        comments: data.comments,
        terms_conditions: data.terms_conditions,
      };
      console.log("form data is :", payload);
      const response = await apiService.createChangePujaRequest(payload);

      if (response.data.success) {
        sessionStorage.setItem(
          "successPageState",
          JSON.stringify({
            type: "change-puja-request",
            requestId: response.data.data.puja_request_id,
          })
        );
        navigate("/success", {
          state: {
            type: "change-puja-request",
            requestId: response.data.data.puja_request_id,
          },
        });
      } else {
        setErrorMessage("Error processing request. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting change request:", error);
      setErrorMessage("Error processing request. Please try again.");
    }
  };

  // Calculate min and max dates for the date picker
  const calculateDateRestrictions = () => {
    if (!bookingDetails) return { minDate: '' };
    
    const requestCreatedDate = new Date(bookingDetails.created_at);
    
    // Set min date to 3 days after request creation date
    const minDate = new Date(requestCreatedDate);
    minDate.setDate(requestCreatedDate.getDate() + 3);
    
    return {
      minDate: minDate.toISOString().split('T')[0]
    };
  };

  const dateRestrictions = calculateDateRestrictions();

  return (
    <InnerLayout>
      <Box component="section" className="inner-banner" sx={{ p: 0 }}>
        <Box component="img" className="banner-pic" src={InnerBanner} />
        <Box className="inner-text">
          <Container maxWidth="xl">
            <Grid
              container
              spacing={2}
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Grid item size={{ md: 6 }}>
                <Typography
                  component="h1"
                  variant="h1"
                  className="section-title text-white"
                >
                  Change Puja Request
                </Typography>
              </Grid>
              <Grid item size={{ md: 6 }}>
                <CallforAction />
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>

      <Box component="section">
        <Container maxWidth="xl">
          <Box className="custom-card" sx={{ mx: "auto" }}>
            {/* Show change/cancel note above both forms, support HTML */}
            {changeAndCancelNote && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body1"
                  color="info.main"
                  component="div"
                  dangerouslySetInnerHTML={{ __html: changeAndCancelNote }}
                />
              </Box>
            )}
            {!bookingDetails ? (
              <form onSubmit={handleSubmit(handleSearch)}>
                <Grid container spacing={2}>
                  <Grid item size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="Request ID *"
                      variant="outlined"
                      fullWidth
                      {...register("request_id")}
                      error={!!errors.request_id}
                      helperText={errors.request_id?.message}
                      sx={{ mb: 1 }}
                    />
                  </Grid>
                  <Grid item size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="Email *"
                      variant="outlined"
                      fullWidth
                      {...register("email")}
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      sx={{ mb: 1 }}
                    />
                  </Grid>
                  <Grid item size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="Requestor Name *"
                      variant="outlined"
                      fullWidth
                      {...register("name")}
                      defaultValue={bookingDetails?.name || ""}
                      disabled={!!bookingDetails}
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      sx={{ mt: 3 }}
                    />
                  </Grid>
                  <Grid item size={{ xs: 12, md: 12 }}>
                    {errorMessage && (
                      <Typography color="error">{errorMessage}</Typography>
                    )}
                    <Button type="submit" variant="contained" color="primary">
                      Search
                    </Button>
                  </Grid>
                </Grid>
              </form>
            ) : (
              <Box>
                <Grid container spacing={2}>
                  <Grid item size={{ xs: 12, md: 6 }} order={{ xs: 2, md: 1 }}>
                    <Typography
                      variant="h6"
                      className="text-primary font-700 border-title"
                    >
                      Change Puja Request Form
                    </Typography>

                    <form onSubmit={handleSubmitChange(handleChangeRequest)}>
                      <Grid container spacing={2}>
                        <Grid item size={{ xs: 12, md: 12 }}>
                          <TextField
                            label="Requestor Name *"
                            variant="outlined"
                            fullWidth
                            {...registerChange("name")}
                            defaultValue={bookingDetails?.name || ""}
                            disabled={!!bookingDetails}
                            error={!!errorsChange.name}
                            helperText={errorsChange.name?.message}
                            sx={{ mt: 3 }}
                          />
                        </Grid>
                        <Grid item size={{ xs: 12, md: 6 }}>
                          <TextField
                            type="date"
                            label="New Puja Date *"
                            variant="outlined"
                            fullWidth
                            {...registerChange("new_event_date")}
                            error={!!errorsChange.new_event_date}
                            helperText={errorsChange.new_event_date?.message}
                            sx={{ mt: 3 }}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                              min: dateRestrictions.minDate
                            }}
                          />
                        </Grid>
                        <Grid item size={{ xs: 12, md: 6 }}>
                          <FormControl fullWidth>
                            <InputLabel>Time of Puja *</InputLabel>
                            <Select
                              {...registerChange("time_of_event")}
                              error={!!errorsChange.time_of_event}
                              defaultValue=""
                              sx={{ mt: 3 }}
                            >
                              {timeSlots.map((slot) => (
                                <MenuItem key={slot} value={slot}>
                                  {slot}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item size={{ xs: 12, md: 12 }}>
                          <TextField
                            label="Comments *"
                            variant="outlined"
                            fullWidth
                            multiline
                            rows={3}
                            {...registerChange("comments")}
                            error={!!errorsChange.time_of_event}
                            sx={{ mt: 3 }}
                          />
                        </Grid>
                        <Grid item size={{ xs: 12, md: 6 }}>
                          <TermsConditions type="socialhall" />
                        </Grid>
                        <Grid item size={{ xs: 12, md: 12 }}>
                          <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={isSubmitting} // Disable button when submitting
                            sx={{ mt: 3 }}
                          >
                            {isSubmitting ? "Submitting..." : "Submit Request"} 
                          </Button>
                        </Grid>
                      </Grid>
                    </form>
                  </Grid>

                  <Grid item size={{ xs: 12, md: 6 }} order={{ xs: 1, md: 2 }}>
                    <Box className="bg-light p-30">
                      <Typography
                        variant="h6"
                        className="text-primary font-700 border-title"
                      >
                        Booking Details
                      </Typography>

                      <Box className="address-section">
                        <Typography>
                          <Typography component="span">Name:</Typography>{" "}
                          {bookingDetails.name}
                        </Typography>
                        
                        <Typography>
                          <Typography component="span">Request Id:</Typography>{" "}
                          {bookingDetails.puja_request_id}
                        </Typography>
                        <Typography>
                          <Typography component="span">Email:</Typography>{" "}
                          {bookingDetails.email}
                        </Typography>
                        <Typography>
                          <Typography component="span">Date of Event:</Typography>{" "}
                          {formatDate(bookingDetails.date_of_puja)}
                        </Typography>
                        <Typography>
                          <Typography component="span">Timings of puja:</Typography>{" "}
                          {bookingDetails.time_of_puja}
                        </Typography>
                        <Typography>
                          <Typography component="span">Alternate Date:</Typography>{" "}
                          {formatDate(bookingDetails.alternate_date_of_puja1)}
                        </Typography>
                      </Box>
                      <br />
                      <Typography
                        variant="p"
                        component="p"
                        className="text-primary font-700 border-title mt-30"
                      >
                        Selected pujas:
                      </Typography>

                      {bookingDetails.selected_pujas.map((hall) => (
                        <Box className="hall-list-item" key={hall.id} sx={{ mt: 2 }}>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            {hall.name}
                            <Typography
                              variant="body2"
                              className="font-700 text-primary"
                            >
                              ${hall.puja_cost}
                            </Typography>
                          </Box>
                        </Box>
                      ))}

                      <Box mt={2} display="flex" justifyContent="space-between">
                        <Typography variant="body1" className="font-700">
                          Total Cost:
                        </Typography>
                        <Typography variant="body1" className="font-700 text-primary">
                          ${bookingDetails.selected_pujas.reduce((acc, hall) => acc + parseFloat(hall.puja_cost), 0).toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        </Container>
      </Box>
    </InnerLayout>
  );
}

export default ChangePujaRequest;